import { useCallback } from 'react';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { shouldHandleTheError } from '@atlassian/jira-business-error-handling/src/utils/partial-data/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { getParentIssueId } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import type { ChildrenQueryData } from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import type { UpdaterReturn } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/types.tsx';
import { useViewUpdater } from '@atlassian/jira-business-list-handlers/src/controllers/view-updater/main.tsx';
import { createViewUpdaterCallbacksWithCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/view-updater/utils/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useOverview } from '@atlassian/jira-router-resources-business-overview-details/src/index.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fieldIds } from '../../common/constants.tsx';
import { getVariables } from '../../common/utils/get-variables/index.tsx';
import { useIssueParentMap } from '../../common/utils/use-issue-parent-map.tsx';
import { useIssuesMap } from '../../common/utils/use-issues-map.tsx';
import type {
	JWMTimelineViewItems,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection as JWMTimelineViewItemsEdges,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMTimelineViewItemsNode,
} from '../get-timeline-view-issues/__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from '../get-timeline-view-issues/gql.tsx';
import type { TimelineViewIssuesReturn } from '../get-timeline-view-issues/types.tsx';
import { addChildToParentTimeline } from '../get-timeline-view-issues/utils.tsx';

// Loads individual items, dealing with the cache updates
export const useTimelineViewUpdater = ({
	childrenQueryData,
}: {
	childrenQueryData?: ChildrenQueryData<TimelineViewIssuesReturn>;
}): UpdaterReturn => {
	const issueTypesAndFields = useIssueTypesAndFields({ issueOperation: 'VIEW' });
	const hierarchyLevels = issueTypesAndFields.hierarchyLevels;
	const { quickFiltersJql, hierarchyParent, sorting, jqlContext, isHidingDoneItems } =
		useFilteredViewContextInput({
			view: 'timeline',
		});

	const { addToCache, removeFromCache, updateItemsInCache } = useCacheActions();
	const [{ issueParentMap }] = useIssueParentMap();
	const [{ issuesMap }] = useIssuesMap();
	const cloudId = useCloudId();
	const { data: overview } = useOverview();
	const isOverview = overview != null && Array.isArray(overview.projects);

	let projectKey: string | undefined;
	if (!isOverview) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { key } = useProject();
		projectKey = key;
	}

	const topLevelVariables = {
		...getVariables({
			jqlContext,
			fieldIds,
			quickFiltersJql,
			isHidingDoneItems,
			hierarchyParent,
			...(hierarchyLevels !== undefined && {
				// the above check will be unnecessary once the FF is removed
				hierarchyLevel: hierarchyLevels[0]?.level,
			}),
			sorting,
			...(projectKey ? { projectIdOrKey: projectKey } : {}),
		}),
	};

	const { addToCacheCallback, removeFromCacheCallback } =
		createViewUpdaterCallbacksWithCacheActions<JWMTimelineViewItemsNode, JWMTimelineViewItems>({
			query: TIMELINE_VIEW_ITEMS_QUERY,
			addToCache,
			updateItemsInCache,
			removeFromCache,
			isHierarchyEnabled: () => typeof hierarchyParent !== 'undefined',
			isInfiniteHierarchyEnabled: () => true,
			createVariablesForItemWithoutParent: () => topLevelVariables,
			createVariablesForItemWithParent: (parentIssueId) => {
				if (!hierarchyParent) {
					// this scenario should be impossible because there is isHierarchyEnabled parameter above
					return topLevelVariables;
				}
				return {
					...getVariables({
						jqlContext,
						fieldIds,
						quickFiltersJql,
						isHidingDoneItems,
						sorting,
						hierarchyParent: undefined,
						parentIssue: {
							hierarchyType: hierarchyParent,
							issueId: parentIssueId,
						},
						...(projectKey ? { projectIdOrKey: projectKey } : {}),
					}),
					cloudId,
				};
			},
			// We only want to add or remove item if the existing query for children-of-this-parent exists
			// this is to prevent apollo error where the cache key is missing
			shouldChildItemBeAddedToOrRemovedFromCache: (parentIssueId) =>
				Boolean(childrenQueryData && childrenQueryData[toIssueId(parentIssueId)] !== undefined),
			onUpdateParentWithNewChild: addChildToParentTimeline,
			getParentOfIssueIfExist: (issueId) => {
				const maybeIssue = issuesMap.get(issueId);
				if (!maybeIssue) {
					return undefined;
				}
				return getParentIssueId(maybeIssue);
			},
			getPrevParentOfIssueIfExists: (issueId) => issueParentMap.get(String(issueId)),
		});

	return useViewUpdater<JWMTimelineViewItemsNode, JWMTimelineViewItemsEdges, JWMTimelineViewItems>({
		input: {
			fieldIds,
			quickFiltersJql,
			isHidingDoneItems,
		},
		query: TIMELINE_VIEW_ITEMS_QUERY,
		actions: [{ addToCache: addToCacheCallback, removeFromCache: removeFromCacheCallback }],
		onError: useCallback((apolloError) => {
			if (!shouldHandleTheError(apolloError)) {
				return;
			}
			fireErrorAnalytics({
				meta: {
					id: 'timelineViewUpdaterQuery',
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				attributes: {
					message: 'Failed to fetch issues in timeline view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		}, []),
		onAppendItemsError: useCallback((apolloError) => {
			if (!shouldHandleTheError(apolloError)) {
				return;
			}
			fireErrorAnalytics({
				meta: {
					id: 'timelineViewAppendItemsQuery',
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				attributes: {
					message: 'Failed to append issues by id in timeline view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		}, []),
	});
};
